import React from "react";
import useSWR from "swr";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend,
} from "recharts";
import { Database, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Spinner, Empty, StatusBadge } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

const STATUS_META = {
    ONLINE: { cls: "bg-green-100 text-green-800", label: "ONLINE" },
    SHUNNED: { cls: "bg-yellow-100 text-yellow-800", label: "SHUNNED" },
    OFFLINE_SOFT: { cls: "bg-orange-100 text-orange-800", label: "OFFLINE (soft)" },
    OFFLINE_HARD: { cls: "bg-red-100 text-red-800", label: "OFFLINE" },
};

function StatusPill({ status }) {
    const m = STATUS_META[status] || { cls: "bg-gray-100 text-gray-800", label: status || "?" };
    return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}

const BACKEND_COLORS = {
    "10.101.0.11": "#2563eb",
    "10.101.0.12": "#16a34a",
    "10.101.0.13": "#d97706",
    "10.101.0.14": "#9333ea",
};

export default function DbProxyPanel() {
    const live = useSWR("/api/admin/infra/db-proxy/live", fetcher, { refreshInterval: 15000 });
    const hist = useSWR("/api/admin/infra/db-proxy/history?hours=24", fetcher, { refreshInterval: 60000 });
    const alerts = useSWR("/api/admin/infra/db-proxy/alerts", fetcher, { refreshInterval: 30000 });

    if (live.error) return <Empty icon={Database} title="DB-Proxy nicht erreichbar" subtitle={String(live.error)} />;
    if (!live.data) return <Spinner />;

    const d = live.data;
    const backends = d.backends || [];
    const points = hist.data?.points || [];
    const backendsList = [...new Set(points.map((p) => p.backend))];

    // Chart-Daten: Bucket → {t, "10.101.0.11": qps, ...}
    const chart = Object.values(points.reduce((acc, p) => {
        const t = p.t;
        acc[t] = acc[t] || { t };
        acc[t][p.backend] = Number(p.qps) || 0;
        return acc;
    }, {}));

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Database className="w-5 h-5" /> DB-Proxy (ProxySQL)
                </h3>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span>Client-Verbindungen: <b>{d.client_conns ?? "–"}</b></span>
                    <span>Query-Cache-Einträge: <b>{d.cache_entries ?? "–"}</b></span>
                    <span>Writer online: <b>{d.writers_online}/{d.writers_total}</b></span>
                </div>
            </div>

            {backends.length === 0 ? (
                <Empty icon={Database} title="Keine Backends" subtitle="ProxySQL-Admin nicht erreichbar oder ohne Writer-Hostgroups." />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {backends.map((b) => {
                        const meta = BACKEND_COLORS[b.backend] || "#6b7280";
                        const online = b.status === "ONLINE";
                        return (
                            <div key={b.backend} className="border rounded-lg p-4 space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold flex items-center gap-2">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: meta }} />
                                        {b.backend}
                                    </div>
                                    <StatusPill status={b.status} />
                                </div>
                                <div className="text-sm text-gray-600 space-y-1">
                                    <div>Writer-HG: <b>{b.hostgroup}</b></div>
                                    <div>Clouds (gepinnt): <b>{d.users_per_hg?.[String(b.hostgroup)] ?? 0}</b></div>
                                    <div>Verbindungen: <b>{b.conn_used}</b> aktiv / <b>{b.conn_free}</b> frei</div>
                                    <div>Fehler: <b>{b.conn_err}</b></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                    Queries/s je Backend (24h)
                    {hist.isValidating && <RefreshCw className="w-3 h-3 animate-spin text-gray-400" />}
                </h4>
                {chart.length === 0 ? (
                    <Empty icon={Database} title="Noch keine Metriken" subtitle="Der Sampler sammelt im 60s-Takt — erste Kurve nach wenigen Minuten." />
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={chart} margin={{ left: 0, right: 8, top: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="t" tickFormatter={(t) => String(t).slice(11, 16)} />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            {backendsList.map((b) => (
                                <Area key={b} type="monotone" dataKey={b} stroke={BACKEND_COLORS[b] || "#6b7280"} fill={BACKEND_COLORS[b] || "#6b7280"} fillOpacity={0.15} strokeWidth={2} dot={false} />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Letzte DB-Proxy-Alarme</h4>
                {(alerts.data?.alerts || []).length === 0 ? (
                    <div className="text-sm text-gray-500">Keine Alarme.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-1 pr-3">Zeit</th><th className="pr-3">Ereignis</th><th className="pr-3">Übergang</th><th>Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(alerts.data?.alerts || []).map((a, i) => (
                                <tr key={i} className="border-b last:border-0">
                                    <td className="py-1 pr-3 whitespace-nowrap">{String(a.ts || "").slice(0, 19)}</td>
                                    <td className="pr-3">{a.name}</td>
                                    <td className="pr-3"><StatusBadge status={a.to_state === "ok" ? "healthy" : "critical"} /></td>
                                    <td className="text-gray-600">{a.detail}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
